import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import StreamPanel from "@/components/StreamPanel";
import PreviewPanel from "@/components/PreviewPanel";
import UploadZone from "@/components/UploadZone";
import SpellCheck from "@/components/SpellCheck";
import PromptLangPicker from "@/components/PromptLangPicker";
import SocraticCopilot from "@/components/SocraticCopilot";
import { useIsMobile } from "@/hooks/useIsMobile";
import { GitFork, Mic, Zap, Hammer, ListChecks, Paperclip, Columns2, MessageCircle, ShieldAlert, Gauge, BookOpen, Undo2, Redo2, SlidersHorizontal } from "lucide-react";

// Patch 68 — the Split-View Canvas. Two boxes only: LEFT = prompt box (explanation stream + prompt
// input + a compact icon strip for the Coach, Perimeter Gate, CRX, Fixed-price quote, Pre-mortem and
// Compliance details) and RIGHT = live preview. Every other control moved up to the top toolbar; the
// boxes now fill the screen. All icons are size 14 to match the preview-box icons.
const ICON = 14;

export default function BuilderTab({ projectId, project, desc, onDesc, prefs, onReload, onFeature, onUndo, onRedo, canUndo, canRedo, onSearch }) {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [boost, setBoost] = useState(0);
  const [boostQuote, setBoostQuote] = useState(null);
  const [mode, setMode] = useState("build");
  const [planUsage, setPlanUsage] = useState(null);
  const [plan, setPlan] = useState(null);
  const [attach, setAttach] = useState(false);
  const [dep, setDep] = useState(null);
  const [jumpAt, setJumpAt] = useState(null);

  // Draggable, resizable split (25%–75%), maximise/minimise per column, revert-to-50/50 pill,
  // persisted per user (this browser) per project via localStorage.
  const isMobile = useIsMobile(768);
  const wrapRef = useRef(null);
  const SPLIT_KEY = `kadana_split_pct_${projectId}`;
  const MIN_KEY = `kadana_split_min_${projectId}`;
  const [leftPct, setLeftPct] = useState(() => { try { return Number(localStorage.getItem(SPLIT_KEY)) || 50; } catch { return 50; } });
  const [minimised, setMinimised] = useState(() => { try { return localStorage.getItem(MIN_KEY) || null; } catch { return null; } });
  const [mobileView, setMobileView] = useState("prompt");
  useEffect(() => { try { localStorage.setItem(SPLIT_KEY, String(Math.round(leftPct))); } catch { /* ignore */ } }, [leftPct, SPLIT_KEY]);
  useEffect(() => { try { if (minimised) localStorage.setItem(MIN_KEY, minimised); else localStorage.removeItem(MIN_KEY); } catch { /* ignore */ } }, [minimised, MIN_KEY]);

  // The maximise toggles and Save-to-GitHub now live in the top toolbar; they reach this component via events.
  useEffect(() => {
    const tl = () => setMinimised((m) => (m === "right" ? null : "right"));
    const tr = () => setMinimised((m) => (m === "left" ? null : "left"));
    const gh = () => toast.info("Save to GitHub uses Kadana's universal-export path. It requests only the scope needed to push THIS project's code — never broader account access. Use the ‘Save to GitHub’ action in the chat input to authorise.");
    window.addEventListener("kadana-toggle-left", tl);
    window.addEventListener("kadana-toggle-right", tr);
    window.addEventListener("kadana-save-github", gh);
    return () => { window.removeEventListener("kadana-toggle-left", tl); window.removeEventListener("kadana-toggle-right", tr); window.removeEventListener("kadana-save-github", gh); };
  }, []);

  const onSplitDrag = (e) => {
    e.preventDefault();
    const move = (ev) => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const pct = ((clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.max(25, Math.min(75, pct)));
    };
    const up = () => {
      document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", move); document.removeEventListener("touchend", up);
    };
    document.addEventListener("mousemove", move); document.addEventListener("mouseup", up);
    document.addEventListener("touchmove", move, { passive: false }); document.addEventListener("touchend", up);
  };

  const loadUsage = useCallback(() => { api.get(`/builder/plan-usage/${projectId}`).then((r) => setPlanUsage(r.data)).catch(() => {}); }, [projectId]);
  useEffect(() => { api.get("/builder/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(loadUsage, [loadUsage]);
  useEffect(() => { api.get(`/hosting/${projectId}/status`).then((r) => setDep((r.data.deployments || [])[0] || null)).catch(() => {}); }, [projectId]);

  const quoteBoost = async (pct) => {
    setBoost(pct);
    try { const { data } = await api.post("/builder/token-boost/quote", { base_credits: 50, boost_pct: pct }); setBoostQuote(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runPlan = async () => {
    try { const { data } = await api.post("/builder/plan", { project_id: projectId, prompt: desc }); setPlan(data); loadUsage(); toast.success(`Plan ready · ${data.remaining} free left today`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const fork = async () => {
    try { const { data } = await api.post("/builder/fork", { project_id: projectId }); toast.success(data.note); nav(`/workspace/project/${data.new_project_id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const voiceInput = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const { data } = await api.post("/media/transcribe", { project_id: projectId, upload_id: "mic-live", media_type: "audio", consent_given: true });
      toast.info(data.note || "Voice captured (SANDBOX). Uses the same M3 speech-to-text provider.");
    } catch { toast.error("Microphone permission is needed for voice input."); }
  };
  const openPreview = () => { if (dep?.subdomain) window.open(`https://${dep.subdomain}`, "_blank"); else toast.info("Provision a deployment to open a live preview."); };
  const publish = async () => {
    if (!dep) { nav(`/workspace/project/${projectId}`); toast.info("Open Checkpoints & Publish, or provision hosting to publish."); return; }
    try { const { data } = await api.post(`/hosting/${dep.id}/publish`, {}); toast.success(data.note || "Published"); onReload?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toolBtn = (key, label, Icon, extra = {}) => (
    <button key={key} title={label} aria-label={label} data-testid={`feature-${key}`} className="k-btn k-btn-ghost"
      style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => onFeature?.(key)} {...extra}><Icon size={ICON} /></button>
  );

  return (
    <div data-testid="builder-three-zone" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
      {isMobile && (
        <div data-testid="mobile-split-switch" style={{ display: "flex", border: "1px solid var(--control)", borderRadius: 8, overflow: "hidden", marginBottom: 10 }}>
          <button className={`k-btn ${mobileView === "prompt" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="mobile-view-prompt" onClick={() => setMobileView("prompt")} style={{ flex: 1 }}>Prompt</button>
          <button className={`k-btn ${mobileView === "preview" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="mobile-view-preview" onClick={() => setMobileView("preview")} style={{ flex: 1 }}>Preview</button>
        </div>
      )}
      {!isMobile && minimised && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
          <button className="k-badge k-badge-accent" data-testid="revert-split" onClick={() => setMinimised(null)} style={{ cursor: "pointer", padding: "6px 14px", display: "inline-flex", alignItems: "center", gap: 6 }}><Columns2 size={ICON} /> Revert to Split View</button>
        </div>
      )}
      <div ref={wrapRef} style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: isMobile ? "60vh" : 0 }}>
        {/* LEFT: prompt box — explanation stream (top) + prompt input + compact feature icon strip (bottom) */}
        <div data-testid="split-left" style={{ ...(minimised === "right" ? { flex: "1 1 auto" } : { flex: `0 0 calc(${leftPct}% - 5px)` }), minWidth: 0, display: ((isMobile && mobileView !== "prompt") || minimised === "left") ? "none" : "flex", flexDirection: "column" }}>
          <div data-testid="explanation-stream" style={{ flex: 1, overflowY: "auto", marginBottom: 12, minHeight: 0 }}>
            <StreamPanel projectId={projectId} mode={prefs?.explanation_mode} onJump={(e) => setJumpAt(e)} />
            {plan && (
              <div className="k-container" data-testid="plan-output" style={{ padding: 14, marginTop: 10 }}>
                <strong style={{ fontSize: 13 }}>Plan (free — no credits charged)</strong>
                <ol className="k-muted" style={{ fontSize: 13, marginTop: 6, paddingLeft: 18 }}>{plan.plan.map((s, i) => <li key={i} style={{ marginBottom: 3 }}>{s}</li>)}</ol>
                <p className="k-muted" style={{ fontSize: 11 }}>{plan.note}</p>
              </div>
            )}
          </div>

          {/* PROMPT BOX anchored at the bottom */}
          <div className="k-container" data-testid="prompt-box" style={{ padding: 14 }}>
            {/* Feature icon strip — Coach, Perimeter Gate, CRX, Fixed-price quote, Pre-mortem, Compliance details + prompt tools */}
            <div data-testid="prompt-tools" style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
              {toolBtn("coach", "BeCo Coach", MessageCircle)}
              {toolBtn("perimeter", "Regulatory perimeter gate", ShieldAlert)}
              {toolBtn("crx", "Compliance Risk Index", Gauge)}
              {toolBtn("quote", "Fixed-price quote & sandbox build", Hammer)}
              {toolBtn("premortem", "Compliance pre-mortem", ListChecks)}
              {toolBtn("details", "Compliance & project details", SlidersHorizontal)}
              <span style={{ width: 1, height: 18, background: "var(--border)", margin: "0 4px" }} aria-hidden="true" />
              <button className="k-btn k-btn-ghost" data-testid="open-registry" title="Search & Discovery" aria-label="Search & Discovery" style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => onSearch?.()}><BookOpen size={ICON} /></button>
              <button className="k-btn k-btn-ghost" data-testid="desc-undo" title="Undo" aria-label="Undo" style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => onUndo?.()} disabled={!canUndo}><Undo2 size={ICON} /></button>
              <button className="k-btn k-btn-ghost" data-testid="desc-redo" title="Redo" aria-label="Redo" style={{ padding: "6px 8px", minHeight: 0 }} onClick={() => onRedo?.()} disabled={!canRedo}><Redo2 size={ICON} /></button>
            </div>
            <textarea className="k-control" data-testid="builder-prompt" rows={3} value={desc}
              onChange={(e) => onDesc(e.target.value)} placeholder="Describe what you want in plain English…" />
            <SpellCheck text={desc} onApply={onDesc} apiPath="/lt/check" />
            <div data-testid="prompt-language" style={{ marginTop: 8 }}><PromptLangPicker /></div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
              <div style={{ display: "flex", border: "1px solid var(--control)", borderRadius: 8, overflow: "hidden" }}>
                <button className={`k-btn ${mode === "build" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="mode-build" onClick={() => setMode("build")}><Hammer size={ICON} /> Build</button>
                <button className={`k-btn ${mode === "plan" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="mode-plan" onClick={() => setMode("plan")}><ListChecks size={ICON} /> Plan</button>
              </div>
              <label className="k-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                <Zap size={ICON} /> Boost
                <select className="k-control" data-testid="token-boost" style={{ width: "auto", padding: "4px 6px" }} value={boost} onChange={(e) => quoteBoost(Number(e.target.value))}>
                  <option value={0}>0%</option>
                  {(cfg?.token_boost_levels_pct || []).map((p) => <option key={p} value={p}>{p}%</option>)}
                </select>
              </label>
              <button className="k-btn k-btn-ghost" data-testid="attach-toggle" onClick={() => setAttach((a) => !a)}><Paperclip size={ICON} /> Attach</button>
              <button className="k-btn k-btn-ghost" data-testid="voice-input" onClick={voiceInput}><Mic size={ICON} /> Voice</button>
              <button className="k-btn k-btn-ghost" data-testid="fork-conversation" onClick={fork}><GitFork size={ICON} /> Fork</button>
              {mode === "build"
                ? <button className="k-btn k-btn-primary" data-testid="run-build-builder" style={{ marginLeft: "auto" }} onClick={() => toast.info("Build consumes credits — confirm the fixed-price quote below to generate.")}>Build</button>
                : <button className="k-btn k-btn-primary" data-testid="run-plan" style={{ marginLeft: "auto" }} onClick={runPlan}>Plan (free)</button>}
            </div>
            {boost > 0 && boostQuote && (
              <p className="k-muted" data-testid="boost-quote" style={{ fontSize: 12, marginTop: 8 }}>
                +{boost}% boost → {boostQuote.boosted_credits} credits ({boostQuote.extra_credits} extra). Margin {boostQuote.margin_pct}% {boostQuote.margin_ok ? "✓ within floor" : "⚠ below floor"}.
              </p>
            )}
            {mode === "plan" && planUsage && (
              <p className="k-muted" data-testid="plan-usage" style={{ fontSize: 12, marginTop: 6 }}>Plan mode is free to you — {planUsage.remaining} of {planUsage.allowance} left today for this project.</p>
            )}
            {attach && <div style={{ marginTop: 10 }}><UploadZone projectId={projectId} /></div>}
          </div>
        </div>

        {!isMobile && !minimised && (
          <div role="separator" aria-label="Resize columns" data-testid="split-handle" onMouseDown={onSplitDrag} onTouchStart={onSplitDrag}
            style={{ flex: "0 0 10px", cursor: "col-resize", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ width: 3, height: 46, borderRadius: 999, background: "var(--border-strong)" }} />
          </div>
        )}

        {/* RIGHT: live preview + Socratic AI Co-Pilot overlay (closed by default, sits below the preview) */}
        <div data-testid="split-right" style={{ ...(minimised === "left" ? { flex: "1 1 auto" } : { flex: "1 1 0" }), minWidth: 0, position: "relative", display: ((isMobile && mobileView !== "preview") || minimised === "right") ? "none" : "flex", flexDirection: "column" }}>
          <PreviewPanel projectId={projectId} dep={dep} project={project} jumpAt={jumpAt}
            onReload={onReload} onOpen={openPreview} onPublish={publish} />
          <SocraticCopilot projectId={projectId} prompt={desc}
            deferToGate={(project?.perimeter_outcome === "red") || (boost > 0 && !!boostQuote && !boostQuote.margin_ok)} />
        </div>
      </div>
    </div>
  );
}
